import classNames from "classnames";
import * as React from "react";
import { Checkbox, Input, Label, Select } from "uxp/components";
import { hasValue, parseValue } from "../../../../util";
import { IBaseFieldProperty, IPropertyEditorProps, IUIFieldProps, registerUIField } from "../../common";
import { CustomColorPicker, escapeUnit, Property, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle, TextAlignEditor, TypeSelectorComponent } from "../../ui-props/common";
import ActionEditorLink, { IAction } from "../../action-editor/ActionEditor";
import IconEditor, { generateIconProp } from "../../icon-selector/IconEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionRenderComponent from "../../components/ActionRenderComponent";
import { WidgetDesignerPillInput } from "../../pill-input/PillInput";

type LabelStyleType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'custom' | 'text'

interface ILabelStyle {
	name: LabelStyleType,
	label: string,
	styles: any
}

const LabelStyles: { [styleName: string]: ILabelStyle } = {
	'text': {
		name: 'text',
		label: 'Text',
		styles: {
			fontWeight: 500,
			fontSize: 10
		}
	},
	'custom': {
		name: 'custom',
		label: 'Custom Styles',
		styles: {
			fontWeight: 500
		}
	},
	'h1': {
		name: 'h1',
		label: 'Header 1',
		styles: {
			fontWeight: 700,
			fontSize: 17
		}
	},
	'h2': {
		name: 'h2',
		label: 'Header 2',
		styles: {
			fontWeight: 600,
			fontSize: 15
		}
	},
	'h3': {
		name: 'h3',
		label: 'Header 3',
		styles: {
			fontWeight: 500,
			fontSize: 13
		}
	},
	'h4': {
		name: 'h4',
		label: 'Header 4',
		styles: {
			fontWeight: 500,
			fontSize: 10
		}
	},
}


interface ILabelFieldProperty extends IBaseFieldProperty {
	text: string,
	fontSize?: string,
	color?: string,
	backgroundColor?: string
	textAlign?: string,

	label: string,
	hideLabel?: boolean,

	onClick?: IAction,

	icon?: string,
	iconPosition?: 'left' | 'right'
	// iconSize?: string

	design?: 'default' | 'pill'
	style?: LabelStyleType
}

export const Field: React.FunctionComponent<IUIFieldProps<ILabelFieldProperty>> = (props) => {

	let { text, fontSize, color, backgroundColor, textAlign, label, contextData, hideLabel, design, icon, iconPosition, style, onClick } = props.fieldProps || {}

	let styles: React.CSSProperties = { 'boxSizing': 'border-box', ...(LabelStyles[style]?.styles || {}) }

	if (hasValue(fontSize)) styles.fontSize = fontSize
	if (hasValue(color)) styles.color = parseValue(color, props.contextData)
	if (hasValue(textAlign)) styles.textAlign = textAlign as any
	if (hasValue(backgroundColor)) styles.backgroundColor = parseValue(backgroundColor, props.contextData)

	let iconJSX = <FontAwesomeIcon
		style={iconPosition == 'right' ? { marginLeft: '8px' } : { marginRight: '8px' }}
		icon={generateIconProp(icon)}
	/>


	let [action, setAction] = React.useState<IAction>(null)

	return (
		<div className={classNames('ui-field', props.mode)} >
			{label && !hideLabel && <div className='ui-field-label'>{parseValue(label, contextData)}</div>}

			<div onClick={() => setAction(onClick)}>
				<Label className={classNames("widget-designer-field-label", design, style)} styles={styles}>
					{icon && (!iconPosition || iconPosition == 'left') && iconJSX}
					{parseValue(text, contextData)}
					{icon && iconPosition == 'right' && iconJSX}
				</Label>
			</div>
			<ActionRenderComponent
				designer={null}
				uxpContext={props.uxpContext}
				action={action}
				contextData={contextData}
				onClose={() => { setAction(null) }}
				onReloadWidget={() => {
					if (props.uiProps.onReloadWidget) props.uiProps.onReloadWidget()
				}}
			/>


		</div >
	)
}


export const PropertyEditor: React.FunctionComponent<IPropertyEditorProps<ILabelFieldProperty>> = (props) => {
	const { uiProps } = props

	function changeDesign(design: 'pill' | 'default' | string) {
		if (design === 'pill') {
			props.onChange(Object.assign({}, uiProps, {
				hideLabel: true,
				backgroundColor: '#C9517C',
				color: '#FFFFFF',
				design: 'pill',
			}))
		}
		else {
			props.onChange(Object.assign({}, uiProps, {
				hideLabel: false,
				backgroundColor: '#FFFFFF',
				color: '#000000',
				design,
			}))
		}
	}

	function changeStyles(styleName: LabelStyleType) {
		const labelStyles = LabelStyles[styleName];
		if (!labelStyles) return

		props.onChange(Object.assign({}, uiProps, {
			fontSize: labelStyles.styles.fontSize,
			style: styleName,
			hideLabel: true
		}))
	}

	return <PropertyContainer>

		<PropertyGroup>
			<PropertyGroupTitle>Design</PropertyGroupTitle>

			<Property>
				<TypeSelectorComponent
					selected={props.uiProps.design || 'default'}
					onChange={s => changeDesign(s as any)}
					itemWidth="20px"
					iconStyles={{ height: '20px', width: "20px" }}
					types={[
						{ icon: 'bold', label: "Default", value: 'default' },
						{ icon: 'bootstrap', label: "Pill Style", value: 'pill' },
					]} />
			</Property>
		</PropertyGroup>

		<PropertyGroup>
			<PropertyGroupTitle>Text/Label</PropertyGroupTitle>

			<Property>
				<PropertyTitle>Hide the label</PropertyTitle>
				<Checkbox
					label="Hidden"
					checked={!!props.uiProps.hideLabel}
					onChange={s => props.onChange(Object.assign({}, uiProps, { hideLabel: s }))}
				/>
			</Property>
			{!props?.uiProps?.hideLabel &&
				<Property>
					<PropertyTitle>Label</PropertyTitle>
					<WidgetDesignerPillInput
						value={props.uiProps.label || ''}
						onChange={s => props.onChange(Object.assign({}, uiProps, { label: s }))}
						contextData={props.contextData}
					/>
				</Property>
			}
			<Property>
				<PropertyTitle>Text</PropertyTitle>
				<WidgetDesignerPillInput
					value={uiProps.text || ''}
					onChange={s => props.onChange(Object.assign({}, uiProps, { text: s }))}
					contextData={props.contextData}
				/>
			</Property>
		</PropertyGroup>

		<PropertyGroup>
			<PropertyGroupTitle>Style</PropertyGroupTitle>

			<Property>
				<Select
					selected={uiProps?.style || 'custom'}
					options={Object.values(LabelStyles)}
					labelField="label"
					valueField="name"
					onChange={s => {
						changeStyles(s as any);
					}}
					className="widget-designer-input"
				/>
			</Property>
		</PropertyGroup>



		<PropertyGroup>
			<PropertyGroupTitle>Font</PropertyGroupTitle>

			<Property>
				<PropertyTitle>Font Size (px) </PropertyTitle>
				<Input
					type='number'
					value={escapeUnit(`${uiProps.fontSize || '12'}`, 'px')}
					onChange={s => {
						let num = Number(s) || 0
						if (num < 8) s = '8'
						else if (num > 200) s = '200'

						props.onChange(Object.assign({}, uiProps, { fontSize: s + 'px' }))
					}}
					inputAttr={{
						max: '200',
						min: '8'
					}}
					className="widget-designer-input"
				/>
			</Property>

		</PropertyGroup>
		<PropertyGroup>
			<PropertyGroupTitle>Custom Styles</PropertyGroupTitle>

			<Property>
				<PropertyTitle>Color</PropertyTitle>
				<CustomColorPicker contextData={props.contextData}
					color={uiProps.color}
					onChange={s => props.onChange(Object.assign({}, uiProps, { color: s }))}
				/>
			</Property>

			<Property>
				<PropertyTitle>Background Color</PropertyTitle>
				<CustomColorPicker contextData={props.contextData}
					color={uiProps.backgroundColor}
					onChange={s => props.onChange(Object.assign({}, uiProps, { backgroundColor: s }))}
				/>
			</Property>
			<Property>
				<PropertyTitle>Align</PropertyTitle>
				<TextAlignEditor
					value={uiProps.textAlign || ''}
					onChange={s => props.onChange(Object.assign({}, uiProps, { textAlign: s }))}
				/>
			</Property>
		</PropertyGroup>

		<PropertyGroup>
			<PropertyGroupTitle>Icon</PropertyGroupTitle>
			<Property>
				<PropertyTitle>Icon </PropertyTitle>
				<IconEditor
					value={uiProps.icon}
					onChange={s => props.onChange(Object.assign({}, uiProps, { icon: s }))}
				/>
			</Property>

			<Property>
				<PropertyTitle>Icon Position </PropertyTitle>
				<Select
					selected={uiProps.iconPosition || 'left'}
					options={[
						{ label: 'Left', value: 'left' },
						{ label: 'Right', value: 'right' },
					]}
					onChange={s => props.onChange(Object.assign({}, uiProps, { iconPosition: s }))}
					className="widget-designer-input"
				/>
			</Property>
		</PropertyGroup>

		<PropertyGroup>
			<PropertyGroupTitle>On Click</PropertyGroupTitle>

			<Property>
				<ActionEditorLink
					uiProps={props.parentUIProps}
					uxpContext={props?.uxpContext}
					action={props?.uiProps.onClick}
					onChange={s => props.onChange(Object.assign({}, props?.uiProps, { onClick: s }))}
					contextData={props?.contextData}
				/>
			</Property>
		</PropertyGroup>


	</PropertyContainer>;
}
registerUIField({
	id: 'label',
	description: 'Text Label',
	field: Field,
	propertyEditor: PropertyEditor,
	defaultProps: {
		text: 'My information goes here',
		label: 'My Label',
		design: 'default',
		style: 'custom'
	}

});


